import { z } from 'zod';

import { getIsPdfLocationAnchor } from '../../extract-paragraphs/pdf/locationAnchor';
import type {
  BlockType,
  CellRange,
  ExtractedTextSegment,
  Section,
} from '../../extract-paragraphs/shared/types';
import { SELECTION_RANGE_END, SELECTION_RANGE_START } from '../../llm-range-markers';
import type { AlineEditor, MakeSuggestionSource } from '../../slate-api';
import { formatAnchorId, generateSuggestedEditGroupId } from '../../slate-api';
import type { CodeBlockRedlineEditError } from '../redline-shapes';

export type ParagraphLocationAnchor = string;

export type ParagraphInfo = {
  cellRanges: CellRange[];
  documentId: string;
  documentName: string;
  /** Full text including all segments (insert, remove, none). */
  fullText: string;
  kind: 'paragraph';
  listLevel: number;
  listMarker: string;
  locationAnchor: ParagraphLocationAnchor;
  locationLink: string;
  // Extended fields from ExtractedParagraph for future features (e.g., smart citation display)
  section: Section;
  segments: ExtractedTextSegment[];
  /**
   * Edited text (excluding removed segments). Used for diff display, and the
   * projection every applier's `textHash` fingerprints — stale checks must
   * hash this, not `fullText`.
   */
  text: string;
  type: BlockType;
  versionId: string;
};

/**
 * A table or table-row anchor, keyed in the lookup by the prefixed id exactly
 * as the LLM sees it in the document context (`table-…` / `row-…`). Carries
 * what the table-structure suggestion handlers need without the paragraph
 * fields: the contained paragraph anchors for building preview text.
 */
export type BlockAnchorInfo = {
  documentId: string;
  kind: 'row' | 'table';
  /**
   * The unprefixed anchor — a locationId (DOCX) or coordinate string (PDF).
   * The prefixed LLM-facing id is derived via `formatAnchorId(kind, …)`.
   */
  locationAnchor: string;
  /**
   * Ready-to-render preview `<table>` HTML for the chat card; null for DOCX
   * anchors, whose cards derive their preview from the applied edit instead.
   */
  pdfPreviewHtml: string | null;
  versionId: string;
};

export type AnchorInfo = BlockAnchorInfo | ParagraphInfo;

/**
 * The read side of the anchor state the appliers resolve targets against.
 * Keys are the ids exactly as the LLM-facing document format serializes them —
 * bare location anchors for paragraphs, prefixed `table-…` / `row-…` ids for
 * tables and rows. The server's `createParagraphState` return value satisfies
 * this; a test can satisfy it with a Map.
 */
export type ParagraphStateReader = {
  /**
   * All loaded anchors for a document in document order — paragraph anchors
   * first, then the prefixed table/row ids. Used as a document-order index
   * and for diagnostics when a lookup fails.
   */
  getAllLocationAnchorsForDocument(documentId: string): string[];
  getBlockAnchorInfoByAnchorIdWithLazyLoad(
    documentId: string,
    anchorId: string
  ): Promise<BlockAnchorInfo | undefined>;
  getParagraphInfoByLocationAnchorWithLazyLoad(
    documentId: string,
    locationAnchor: string
  ): Promise<ParagraphInfo | undefined>;
};

/**
 * Why a document must not receive new edits: it was sent for signature, or it
 * has been signed (fully or by any one signer). Sending is the point of no
 * return — any signature after that only deepens the lock. The server derives
 * this from the workflow/document state (see `getDocumentSigningLockState`).
 */
export type DocumentSigningLockState = 'sent-for-signature' | 'signed';

/**
 * Provides the editor a suggestion is applied into. On the server this is the
 * request-scoped collab editor pool; a client host can resolve it to the live
 * local editor.
 */
export type SuggestionEditorPool = {
  getOrCreateEditor(documentId: string): Promise<AlineEditor>;
  /**
   * Whether the document is signing-locked (sent for signature or signed) and
   * must not receive edits. The appliers consult it before every materialize
   * so signing-locked documents get the chat-only treatment; preview mode
   * never asks.
   */
  getSigningLockState(documentId: string): Promise<DocumentSigningLockState | null>;
};

/** The host's logger. The server passes its winston logger. */
export type ApplierLogger = {
  error(message: string, meta?: unknown): void;
  warn(message: string): void;
};

/** The host's error reporter. The server passes its Sentry module. */
export type ApplierErrorReporter = {
  captureException(error: unknown, context: { extra: Record<string, unknown> }): void;
  captureMessage(
    message: string,
    context: { extra: Record<string, unknown>; level: 'error' | 'info' | 'warning' }
  ): void;
};

export type HtmlEditApplierDeps = {
  editorPool: SuggestionEditorPool;
  errorReporter: ApplierErrorReporter;
  logger: ApplierLogger;
  /**
   * Builds the source stamp for each suggestion group. The chat host closes
   * over its messageId/threadId; other hosts close over their own lineage.
   */
  makeSource: MakeSuggestionSource;
  paragraphState: ParagraphStateReader;
  userId: string;
};

/** `previewHtmlEdit` never opens an editor, so it doesn't ask for the pool. */
export type PreviewHtmlEditDeps = Omit<HtmlEditApplierDeps, 'editorPool'>;

/**
 * Whether an applier writes the edit into the editor or only builds the card.
 * Materialize auto-downgrades per edit to the preview path when the target is
 * a read-only PDF anchor.
 */
export type HtmlEditApplierMode = 'materialize' | 'preview';

/**
 * Defensive: strip any v2 selection markers the LLM may have echoed
 * from the input prompt. The output format uses the redline range
 * markers for change zones and the selection markers shouldn't appear
 * in generated HTML — but if they do, drop the glyphs so they don't
 * end up as literal text in the redline.
 */
export function stripSelectionMarkers(html: string): string {
  return html.replace(new RegExp(`[${SELECTION_RANGE_START}${SELECTION_RANGE_END}]`, 'g'), '');
}

/**
 * The anchor to stamp on the client card for a table/row suggestion. PDF
 * cards carry the element's own bare rect anchor — the client's coordinate
 * branch (`getIsPdfLocationAnchor` → scroll) requires the 5-part form and
 * jumps to the table/row itself. DOCX cards carry the prefixed `table-…` /
 * `row-…` id: the client navigates DOCX cards by groupId (the suggestion
 * units derived from the document's own marks select the affected rows), so
 * the anchor is display/diagnostics only and the prefixed form says what it
 * names.
 */
export function getBlockCardAnchor(blockAnchorInfo: BlockAnchorInfo): string {
  if (getIsPdfLocationAnchor(blockAnchorInfo.locationAnchor)) return blockAnchorInfo.locationAnchor;
  return formatAnchorId(blockAnchorInfo.kind, blockAnchorInfo.locationAnchor);
}

/**
 * Validation-equivalent of the server's `documentIdSchema`
 * (`core/DocumentIdWithVersion`): that schema is the same `z.uuid()` plus an
 * LLM-facing description that doesn't affect parsing. Every applier validates
 * the model-supplied id with this before touching the document, because an
 * unresolvable target is a per-edit problem rather than a turn failure.
 */
export const documentIdSchema = z.uuid();

/**
 * A user Stop reaches the appliers as either a DOMException abort (from the
 * aborted fetch/signal) or a Temporal CancelledFailure/TimeoutFailure. Either
 * must propagate so the turn cancels. It must never be swallowed into a
 * per-edit `redline-edit-error`.
 */
export function getIsCancellationError(error: unknown): boolean {
  if (error instanceof DOMException) return error.name === 'AbortError';
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'AbortError' ||
    error.name === 'CancelledFailure' ||
    error.name === 'TimeoutFailure'
  );
}

/**
 * Build the typed row that reports one unusable edit. Appliers return this
 * instead of throwing so a single bad anchor or id costs one edit and the rest
 * of the stream still applies.
 */
export function createRedlineEditError({
  documentId,
  locationAnchor,
  message,
}: {
  documentId: string;
  locationAnchor: string;
  message: string;
}): CodeBlockRedlineEditError {
  return {
    documentId,
    groupId: generateSuggestedEditGroupId(),
    locationAnchor,
    message,
    type: 'redline-edit-error',
  };
}
